<template>
    <div class="modal-card">
        <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
            <header class="modal-card-head has-background-white">
                <h3 class="modal-card-title has-text-centered">
                    Editar Recurrencia
                </h3>
                <button
                    class="delete"
                    aria-label="Cerrar Modal"
                    @click="$emit('close')"
                />
            </header>
            <section class="modal-card-body has-background-white has-text-left">
                <div class="columns is-multiline is-vcentered">
                    <div class="column is-12 is-6-desktop">
                        <div class="columns is-vcentered">
                            <div class="column is-5 my-0">
                                {{
                                    isSalary
                                        ? "Nombre Trabajador"
                                        : "Contraparte"
                                }}
                            </div>
                            <div
                                class="column is-7 is-uppercase has-text-weight-bold"
                            >
                                {{ modalData.counterpart.name }}
                            </div>
                        </div>
                    </div>
                    <div class="column is-12 is-6-desktop">
                        <div class="columns is-vcentered">
                            <div class="column is-5 my-0">
                                {{
                                    isSalary ? "Rut Trabajador" : "Rut Empresa"
                                }}
                            </div>
                            <div class="column is-7 has-text-weight-bold">
                                {{ modalData.counterpart.rut }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="columns is-multiline is-vcentered mt-4 py-4 bt">
                    <div class="column is-12 is-6-desktop">
                        <b-field
                            :label="
                                isSalary 
                                    ? 'Tipo de Contrato'
                                    : 'Tipo de Recurrencia'
                            "
                            label-for="indefinite_term"
                        >
                            <b-select
                                id="indefinite_term"
                                placeholder="Seleccione tipo de contrato"
                                expanded
                                v-model="edit.indefinite_term"
                                :disabled="endRecurrency || wasIndefinite"
                            >
                                <option value="0" :disabled="wasIndefinite">
                                    Plazo Fijo
                                </option>
                                <option value="1">Plazo Indefinido</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column is-12 is-6-desktop">
                        <b-field
                            label="Fecha de Término"
                            label-for="valid_until"
                        >
                            <b-datepicker
                                id="valid_until"
                                :placeholder="
                                    isIndefinite
                                        ? ''
                                        : 'Selecciona fecha de término de contrato'
                                "
                                v-bind="initDatepicker"
                                v-model="edit.valid_until"
                                :min-date="minValidFrom"
                                :date-formatter="formatValidUntilInput"
                                :disabled="isIndefinite || endRecurrency"
                            />
                        </b-field>
                    </div>
                    <div class="column is-12 is-6-desktop">
                        <b-field
                            :label="
                                isSalary
                                ? 'Fecha de Ingreso'
                                : 'Fecha de Inicio'
                            "
                            label-for="valid_from"
                        >
                            <b-datepicker
                                id="valid_from"
                                placeholder="Selecciona fecha de ingreso del trabajador"
                                v-bind="initDatepicker"
                                v-model="edit.valid_from"
                                :min-date="today"
                                :disabled="isIndefinite || endRecurrency"
                            />
                        </b-field>
                    </div>
                    <div class="column is-12 is-6-desktop">
                        <InputWithValidation
                            :label="
                                isSalary
                                ? 'Monto líquido recurrente'
                                : 'Monto recurrente'
                            "
                            label-for="amount"
                            id="amount"
                            v-model.lazy="edit.amount"
                            v-money="moneyConfig"
                            rules="required|positive-money|max:13"
                            :disabled="endRecurrency"
                        />
                    </div>
                </div>
                <div v-if="isSalary" 
                    class="columns mt-2 py-4 bt is-align-content-flex-start">
                    <div class="column is-12 is-4-desktop">
                        <b-field>
                            <b-checkbox
                                :disabled="hasBeenTerminated"
                                v-model="endRecurrency"
                                class="has-text-weight-semibold"
                                @change.native="onChangeEndRecurrency()"
                            >
                                Finiquitar Trabajador
                            </b-checkbox>
                        </b-field>
                    </div>
                    <div class="column is-12 is-8-desktop" v-if="endRecurrency">
                        <label for="lastDate">
                            Fecha de último día de trabajo
                        </label>
                        <b-datepicker
                            placeholder="Selecciona último día de trabajo"
                            v-bind="initDatepicker"
                            v-model="lastDate"
                            :min-date="today"
                            @input="onChangeLastDate()"
                        />
                    </div>
                </div>
                <div class="columns is-mobile mt-2 mx-0">
                    <div class="column is-12 has-background-light">
                        <p v-if="isIndefinite && isSalary">
                            <strong class="has-text-weight-semibold">
                                La obligación de contrato indefinido se agregará
                                cada mes de forma indefinida
                            </strong>
                            hasta que se finiquite la obligación.
                        </p>
                        <p v-else-if="isIndefinite && !isSalary">
                            Modificar el monto de la recurrencia, 
                            modificará todos los montos futuros 
                            de esta obligación recurrente.
                        </p>
                        <p v-else>
                            La obligación de contrato a plazo se agregará cada
                            mes hasta la fecha de término designada.
                        </p>
                    </div>
                </div>
            </section>
            <footer
                class="modal-card-foot bt-none has-background-white is-justify-content-flex-end"
            >
                <button
                    class="button"
                    @click="$emit('close')"
                    aria-label="Cerrar Modal"
                    role="button"
                >
                    Volver
                </button>
                <button
                    class="button is-primary"
                    aria-label="Modificar Recurrencia"
                    :disabled="loading"
                    role="button"
                    @click="
                        handleSubmit(
                            callUpdateRecurrent(
                                edit,
                                modalData.recurring_payment
                            )
                        )
                    "
                >
                    {{ recurrencyButtonText }}
                </button>
            </footer>
        </ValidationObserver>
    </div>
</template>

<script>
import moment from "moment";
import InputWithValidation from "@/components/inputs/InputWithValidation";
import { ValidationObserver } from "vee-validate";
import { configMoney } from "@/utils/currency";
import { Money } from "@/directives/money";
import { mapActions, mapState } from "vuex";
import { consoleHandlerError, notificate } from "@/utils/message";
import { getSuccessMessage } from "@/utils/taxDocumentMessage";
import { transformDataToUpdate, getMinValidFrom } from "@/utils/recurrency";

export default {
    name: "RecurrencyEdit",
    components: {
        ValidationObserver,
        InputWithValidation,
    },
    data() {
        return {
            edit: {},
            moneyConfig: configMoney,
            loading: false,
            hasBeenTerminated: null,
            endRecurrency: null,
            lastDate: null,
            myVariable: true,
            today: moment()
                .subtract(1, "days")
                .toDate(),
            initDatepicker: {
                locale: "es",
                "trap-focus": true,
                icon: "calendar-day",
                class: "icon-is-right",
                "nearby-month-days": false,
                "append-to-body": true,
            },
        };
    },
    created() {
        this.edit = {
            indefinite_term: this.modalData.recurring_payment.indefinite_term
                ? "1"
                : "0",
            valid_until: this.moment(
                this.modalData.recurring_payment.valid_until
            ).toDate(),
            valid_from: this.moment(
                this.modalData.recurring_payment.valid_from
            ).toDate(),
            amount: this.modalData.recurring_payment.amount,
            is_terminated: this.modalData.recurring_payment.is_terminated,
        };
        this.endRecurrency = this.modalData.recurring_payment.is_terminated;
        this.hasBeenTerminated = this.modalData.recurring_payment.is_terminated;
        this.lastDate = this.endRecurrency
            ? this.moment(this.modalData.recurring_payment.valid_until).toDate()
            : null;
    },
    methods: {
        ...mapActions("taxDocument", [
            "getTotalPay",
            "getTotalCollect",
            "getAllTaxDocuments",
            "updateRecurrent",
            "terminateRecurrent",
        ]),
        ...mapActions("graphCashFlow", ["getForecastFlowCash"]),
        onChangeLastDate() {
            this.edit.valid_until = this.moment(this.lastDate).toDate();
        },
        onChangeEndRecurrency() {
            if (this.endRecurrency) {
                this.edit.valid_until = this.moment(this.lastDate).toDate();
                this.edit.is_terminated = true;
            } else {
                this.edit.is_terminated = false;
                this.edit.valid_until = this.moment(
                    this.modalData.recurring_payment.valid_until
                ).toDate();
            }
        },
        callUpdateRecurrent(edit, original) {
            const field = transformDataToUpdate(
                edit,
                original,
                this.minValidFrom
            );
            if (field) {
                this.loading = true;
                this.updateRecurrent({
                    id: original.id,
                    field: field,
                })
                    .then(() => {
                        let message = getSuccessMessage(this.modalData);
                        notificate(
                            `<strong>RECURRENCIA ACTUALIZADA</strong><br>La recurrencia del documento
                                ${message} ha sido actualizada exitosamente`,
                            "is-success is-light"
                        );
                        this.updateDataAndClose();
                    })
                    .catch((error) => {
                        consoleHandlerError({ error });
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        updateDataAndClose() {
            this.getTotalPay();
            this.getTotalCollect();
            this.getAllTaxDocuments().catch((error) => {
                consoleHandlerError({ error, empty: true });
            });
            this.getForecastFlowCash().catch((error) => {
                consoleHandlerError({ error });
            });
            this.$emit("close");
        },
        moment(date) {
            return moment(date);
        },
        formatValidUntilInput() {
            if (this.isIndefinite) {
                return ``;
            } else {
                return `${moment(this.edit.valid_until).format("DD/MM/YYYY")}`;
            }
        },
    },
    directives: { money: Money },
    computed: {
        ...mapState("taxDocument", ["modalData"]),
        isSalary: function() {
            return this.modalData.obligation_type.name === "Sueldo";
        },
        minValidFrom: function() {
            return getMinValidFrom(this.edit.valid_from);
        },
        setterValidFrom: function() {
            return this.edit.valid_from;
        },
        wasIndefinite: function() {
            return this.modalData.recurring_payment.indefinite_term;
        },
        isIndefinite: function() {
            return !!Number(this.edit.indefinite_term);
        },
        recurrencyButtonText: function() {
            return this.endRecurrency ? "Finiquitar" : "Actualizar";
        },
    },
    watch: {
        "edit.indefinite_term": function() {
            this.formatValidUntilInput(false);
        },
    },
};
</script>
